import { Component, OnInit } from '@angular/core';
import { DropdownModule } from 'primeng/dropdown';
import { StrategyService } from '../../services/strategy.service';
import { Strategy } from '../../interfaces/models/strategy.model';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ReportsService } from '../../services/reports.service';
import { IUnitWithProject } from '../../interfaces/models/report.model';
import { CommonModule } from '@angular/common';
import { TagModule } from 'primeng/tag';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule } from '@ngx-translate/core';
import { DirectionService } from '../../services/direction.service';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '../../services/auth.service';
import { authUser } from '../../interfaces/auth';

import { NavTitlePipe } from '../../pipes/nav-title.pipe';

@Component({
  selector: 'app-employee-reports',
  standalone: true,
  imports: [
    NavTitlePipe,
    DropdownModule, 
    TagModule, 
    FormsModule, 
    CommonModule, 
    TableModule, 
    ButtonModule, 
    TranslateModule, 
    TooltipModule
  ],
  templateUrl: './employee-reports.component.html',
  styleUrl: './employee-reports.component.scss'
})
export class EmployeeReportsComponent implements OnInit {

  selectedStrategyId = 0;
  year: number | string = 'all';
  strategyOptions = [] as { name: string, strategyId: number }[];
  targetStrategy = {} as Strategy;
  yearsOptions = [] as any[];
  selectedType = "";
  isDownloading = false;
  dir!: 'rtl' | 'ltr';
  
  // Data
  myProjects: IUnitWithProject[] = [];
  myIndicators = [] as any[];
  user!: authUser;

  projectStatusYear: { id: string | number, name: string } = {
    id: 'all',
    name: ""
  }

  constructor(
    private strategyService: StrategyService,
    private reportsService: ReportsService,
    private toastr: ToastrService,
    private directionService: DirectionService,
    private authService: AuthService
  ) {
  }

  ngOnInit(): void {
    // Get current user
    this.authService.authBufferSub$.subscribe((user) => {
      this.user = user as authUser;
    });

    // Strategy options dropdown
    this.strategyService.RawStrategies$.subscribe((strategies: Strategy[]) => {
      this.strategyOptions = strategies.map((x) => ({ name: x.name, strategyId: x.id }))
    })

    this.strategyService.selectedStrategy$.subscribe(selectedStrategy => {
      this.targetStrategy = selectedStrategy || {} as Strategy;
      this.yearsOptions = this.setYears(selectedStrategy?.years || []);
      this.projectStatusYear = this.yearsOptions.find((year: any) => year.id == 'all') as any;
    })

    this.strategyService.defaultStrategy$.subscribe(defaultStrategy => {
      this.targetStrategy = defaultStrategy || {} as Strategy;
      this.selectedStrategyId = defaultStrategy?.id || 0;
      this.yearsOptions = this.setYears(defaultStrategy?.years || []);
      this.projectStatusYear = this.yearsOptions.find((year: any) => year.id == 'all') as any;
    });

    this.directionService.direction$.subscribe((dir) => {
      this.dir = dir;
    })
  }

  setYears(years: number[]): { id: string | number, name: string }[] {
    const yearOptions: { id: string | number, name: string }[] = years.map((year: number) => ({ id: year, name: year.toString() }));
    yearOptions.unshift({ id: 'all', name: this.dir == 'rtl' ? 'الكل' : 'All' });
    return yearOptions;
  }

  strategyOptionsChange() {
    this.strategyService.setStrategyIdfn(this.selectedStrategyId);
  }

  showMyProjectsReport() {
    this.selectedType = "my-projects";
    this.reportsService.getMyProjectsReport(this.selectedStrategyId, this.projectStatusYear.id).subscribe({
      next: (response: any) => {
        this.myProjects = response?.data?.projects || [];
        console.log('My projects:', this.myProjects);
      },
      error: (err) => {
        this.toastr.error(
          this.dir == 'rtl' ? 'فشل تحميل التقرير' : 'Failed to load report',
          this.dir == 'rtl' ? 'خطأ' : 'Error'
        );
      }
    });
  }

  showMyIndicatorsReport() {
    this.selectedType = "my-indicators";
    this.reportsService.getMyIndicatorsReport(this.selectedStrategyId, this.year).subscribe({
      next: (response: any) => {
        this.myIndicators = response?.data?.indicators || [];
        console.log('My indicators:', this.myIndicators);
      },
      error: (err) => {
        this.toastr.error(
          this.dir == 'rtl' ? 'فشل تحميل التقرير' : 'Failed to load report',
          this.dir == 'rtl' ? 'خطأ' : 'Error'
        );
      }
    });
  }

  downloadMyProjectsReport(fileType: string) {
    this.isDownloading = true;
    this.reportsService.downloadMyProjectsReport(this.selectedStrategyId, fileType).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `my-projects-report.${fileType}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.isDownloading = false;
        this.toastr.success(
          this.dir == 'rtl' ? 'تم تحميل التقرير بنجاح' : 'Report downloaded successfully',
          this.dir == 'rtl' ? 'نجاح' : 'Success'
        );
      },
      error: (err) => {
        this.isDownloading = false;
        this.toastr.error(
          this.dir == 'rtl' ? 'فشل تحميل التقرير' : 'Failed to download report',
          this.dir == 'rtl' ? 'خطأ' : 'Error'
        );
      }
    });
  }

  downloadMyIndicatorsReport(fileType: string) {
    this.isDownloading = true;
    this.reportsService.downloadMyIndicatorsReport(this.selectedStrategyId, this.year, fileType).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `my-indicators-report.${fileType}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.isDownloading = false;
        this.toastr.success(
          this.dir == 'rtl' ? 'تم تحميل التقرير بنجاح' : 'Report downloaded successfully',
          this.dir == 'rtl' ? 'نجاح' : 'Success'
        );
      },
      error: (err) => {
        this.isDownloading = false;
        this.toastr.error(
          this.dir == 'rtl' ? 'فشل تحميل التقرير' : 'Failed to download report',
          this.dir == 'rtl' ? 'خطأ' : 'Error'
        );
      }
    });
  }

  printReport(reportType: string) {
    window.print();
  }

  getSeverity(status: string): string {
    const statusLower = status?.toLowerCase() || '';
    
    if (statusLower.includes('مكتمل') || statusLower.includes('completed')) {
      return 'success';
    } else if (statusLower.includes('قيد') || statusLower.includes('progress')) {
      return 'info';
    } else if (statusLower.includes('متوقف') || statusLower.includes('hold')) {
      return 'warning';
    } else if (statusLower.includes('لم') || statusLower.includes('not')) {
      return 'danger';
    }
    
    return 'secondary';
  }
}

